/**
 * WeatherForecastTab
 *
 * This component displays detailed weather forecasts for the next 12 hours for a selected tower,
 * helping with maintenance planning and risk assessment.
 *
 * Key Features:
 * - 12-hour weather forecast visualization
 * - Interactive line charts for key weather parameters:
 *   - Wind Speed trends
 *   - Temperature variations
 *   - Humidity levels
 *   - Precipitation forecast
 * - Detailed forecast data table
 * - Hover tooltips for precise readings
 *
 * Data Visualization:
 * - Line charts using Chart.js
 * - Color-coded parameters:
 *   - Wind Speed: Blue
 *   - Temperature: Red
 *   - Humidity: Green
 *   - Precipitation: Purple
 *
 * Use Cases:
 * - Maintenance scheduling
 * - Work planning
 * - Risk prediction
 * - Resource allocation
 */

"use client"

import type React from "react"
import { View, StyleSheet, ScrollView } from "react-native"
import { Text, useTheme, Card } from "react-native-paper"
import { Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

interface WeatherForecastTabProps {
  forecastData: any[]
  selectedTower: any
}

export const WeatherForecastTab: React.FC<WeatherForecastTabProps> = ({ forecastData, selectedTower }) => {
  const theme = useTheme()

  if (!forecastData || forecastData.length === 0 || !selectedTower) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text>No forecast data available. Please select a tower.</Text>
      </View>
    )
  }

  // Format time labels
  const timeLabels = forecastData.map((item) => {
    const date = new Date(item.time)
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  })

  // Prepare chart data
  const windSpeedData = {
    labels: timeLabels,
    datasets: [
      {
        label: "Wind Speed (km/h)",
        data: forecastData.map((item) => item.values.windSpeed),
        borderColor: "#1890ff",
        backgroundColor: "rgba(24, 144, 255, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  }

  const temperatureData = {
    labels: timeLabels,
    datasets: [
      {
        label: "Temperature (°C)",
        data: forecastData.map((item) => item.values.temperature),
        borderColor: "#f5222d",
        backgroundColor: "rgba(245, 34, 45, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  }

  const humidityData = {
    labels: timeLabels,
    datasets: [
      {
        label: "Humidity (%)",
        data: forecastData.map((item) => item.values.humidity),
        borderColor: "#52c41a",
        backgroundColor: "rgba(82, 196, 26, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  }

  const precipitationData = {
    labels: timeLabels,
    datasets: [
      {
        label: "Precipitation (mm)",
        data: forecastData.map((item) => item.values.precipitationIntensity),
        borderColor: "#722ed1",
        backgroundColor: "rgba(114, 46, 209, 0.2)",
        fill: true,
        tension: 0.4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    interaction: {
      mode: "nearest" as const,
      axis: "x" as const,
      intersect: false,
    },
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <Text variant="headlineMedium">12-Hour Weather Forecast</Text>
        <Text variant="titleMedium">Tower: {selectedTower.name}</Text>
      </View>

      <Card style={styles.chartCard}>
        <Card.Title title="Wind Speed Forecast" />
        <Card.Content style={styles.chartContainer}>
          <Line data={windSpeedData} options={chartOptions} />
        </Card.Content>
      </Card>

      <Card style={styles.chartCard}>
        <Card.Title title="Temperature Forecast" />
        <Card.Content style={styles.chartContainer}>
          <Line data={temperatureData} options={chartOptions} />
        </Card.Content>
      </Card>

      <Card style={styles.chartCard}>
        <Card.Title title="Humidity Forecast" />
        <Card.Content style={styles.chartContainer}>
          <Line data={humidityData} options={chartOptions} />
        </Card.Content>
      </Card>

      <Card style={styles.chartCard}>
        <Card.Title title="Precipitation Forecast" />
        <Card.Content style={styles.chartContainer}>
          <Line data={precipitationData} options={chartOptions} />
        </Card.Content>
      </Card>

      <Card style={styles.forecastTableCard}>
        <Card.Title title="Detailed Forecast Data" />
        <Card.Content>
          <ScrollView horizontal>
            <View style={styles.tableContainer}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Time</Text>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Wind (km/h)</Text>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Temp (°C)</Text>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Humidity (%)</Text>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Precip (mm)</Text>
                <Text style={[styles.tableHeaderCell, { width: 100 }]}>Lightning</Text>
              </View>
              {forecastData.map((item, index) => (
                <View key={index} style={[styles.tableRow, index % 2 === 0 ? { backgroundColor: "#f5f5f5" } : {}]}>
                  <Text style={[styles.tableCell, { width: 100 }]}>{timeLabels[index]}</Text>
                  <Text style={[styles.tableCell, { width: 100 }]}>{item.values.windSpeed}</Text>
                  <Text style={[styles.tableCell, { width: 100 }]}>{item.values.temperature}</Text>
                  <Text style={[styles.tableCell, { width: 100 }]}>{item.values.humidity}</Text>
                  <Text style={[styles.tableCell, { width: 100 }]}>{item.values.precipitationIntensity}</Text>
                  <Text style={[styles.tableCell, { width: 100 }]}>{item.values.lightningStrikeCount}</Text>
                </View>
              ))}
            </View>
          </ScrollView>
        </Card.Content>
      </Card>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 16,
  },
  chartCard: {
    marginBottom: 16,
  },
  chartContainer: {
    height: 300,
  },
  forecastTableCard: {
    marginBottom: 16,
  },
  tableContainer: {
    minWidth: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f0f0f0",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#d9d9d9",
  },
  tableHeaderCell: {
    fontWeight: "bold",
    padding: 8,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#d9d9d9",
  },
  tableCell: {
    padding: 8,
  },
})

